import { useState, useEffect } from 'react';
import { Mail, Edit2, Save, X, Eye, Code, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VARIABLES_DISPONIBLES = [
  { var: '{nombre}', desc: 'Nombre completo del solicitante' },
  { var: '{cedula}', desc: 'Cédula del solicitante' },
  { var: '{email}', desc: 'Email del solicitante' },
  { var: '{link_formulario}', desc: 'Link al Formulario 1' },
  { var: '{link_formulario_2}', desc: 'Link al Formulario 2' },
  { var: '{link_cita}', desc: 'Link para agendar cita' },
  { var: '{link_reagendar}', desc: 'Link para reagendar cita' },
  { var: '{link_firma}', desc: 'Link para firmar declaración jurada' },
  { var: '{numero_expediente}', desc: 'Número del expediente' },
  { var: '{fecha_cita}', desc: 'Fecha de la cita' },
  { var: '{hora_cita}', desc: 'Hora de la cita' },
  { var: '{tipo_cita}', desc: 'Tipo de cita (Virtual/Presencial)' },
  { var: '{link_meet}', desc: 'Link de Google Meet (si es virtual)' },
  { var: '{monto_retiro}', desc: 'Monto calculado del retiro' },
  { var: '{fecha_pago}', desc: 'Fecha en que se realizó el pago' },
  { var: '{cuenta_bancaria}', desc: 'Cuenta bancaria destino (últimos 4 dígitos)' }
];

export default function EmailTemplates({ user }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerarModal, setShowRegenerarModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    nombre: '',
    asunto: '',
    cuerpo: '',
    activo: true
  });
  const [previewMode, setPreviewMode] = useState('edit'); // 'edit' or 'preview'

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/email-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingId(template.id);
    setEditForm({
      nombre: template.nombre,
      asunto: template.asunto,
      cuerpo: template.cuerpo,
      activo: template.activo
    });
    setPreviewMode('edit');
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({ nombre: '', asunto: '', cuerpo: '', activo: true });
    setPreviewMode('edit');
  };

  const handleSave = async () => {
    if (!editForm.nombre || !editForm.asunto || !editForm.cuerpo) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/email-templates/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success('Template actualizado');
        loadTemplates();
        handleCancel();
      } else {
        toast.error('Error al actualizar template');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar');
    }
  };

  const insertVariable = (variable) => {
    setEditForm({
      ...editForm,
      cuerpo: editForm.cuerpo + variable
    });
  };

  const getPreviewHtml = () => {
    return editForm.cuerpo
      .replace(/{nombre}/g, 'Juan Pérez')
      .replace(/{cedula}/g, '12.345.678')
      .replace(/{email}/g, 'juan.perez@email.com')
      .replace(/{link_formulario}/g, 'https://kompa.global/formulario-1')
      .replace(/{link_formulario_2}/g, 'https://kompa.global/formulario-2')
      .replace(/{link_cita}/g, 'https://kompa.global/agendar-cita/123')
      .replace(/{link_reagendar}/g, 'https://kompa.global/reagendar-cita/123')
      .replace(/{link_firma}/g, 'https://kompa.global/firmar/123')
      .replace(/{numero_expediente}/g, 'RET-2025-001')
      .replace(/{fecha_cita}/g, 'Lunes, 15 de Enero 2025')
      .replace(/{hora_cita}/g, '10:00 AM')
      .replace(/{tipo_cita}/g, 'Virtual')
      .replace(/{link_meet}/g, '<a href="https://meet.google.com/abc-defg-hij">Link de Google Meet</a>')
      .replace(/{monto_retiro}/g, '$15,000.00')
      .replace(/{fecha_pago}/g, '25 de Enero 2025')
      .replace(/{cuenta_bancaria}/g, '****4567');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Botón Volver */}
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="border-gray-700 text-gray-300 hover:bg-gray-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Gestión de Templates de Email</h1>
            <p className="text-gray-400">Personaliza los mensajes automáticos del sistema</p>
          </div>
          <Button
            onClick={() => setShowRegenerarModal(true)}
            disabled={regenerating}
            className="bg-purple-500 hover:bg-purple-600 text-white disabled:opacity-50"
          >
            {regenerating ? '🔄 Regenerando...' : '🔄 Regenerar Templates'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de Templates */}
          <div className="lg:col-span-1 space-y-4">
            {templates.map((template) => (
              <Card
                key={template.id}
                className={`bg-gray-800/50 border-gray-700 cursor-pointer transition-all ${
                  editingId === template.id ? 'ring-2 ring-yellow-500' : 'hover:border-yellow-500/50'
                }`}
                onClick={() => handleEdit(template)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4 text-yellow-500" />
                        <h3 className="font-semibold text-white">{template.nombre}</h3>
                      </div>
                      <p className="text-sm text-gray-400 mb-2">{template.asunto}</p>
                      <Badge className={template.activo ? 'bg-green-500' : 'bg-gray-500'}>
                        {template.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    <Edit2 className="w-4 h-4 text-gray-500" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Editor */}
          <div className="lg:col-span-2">
            {editingId ? (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white">Editar Template</CardTitle>
                      <CardDescription className="text-gray-400">
                        Modifica el contenido del email
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewMode(previewMode === 'edit' ? 'preview' : 'edit')}
                        className="border-gray-700 text-white"
                      >
                        {previewMode === 'edit' ? <Eye className="w-4 h-4" /> : <Code className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancel}
                        className="border-gray-700 text-white"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Formulario de edición */}
                  <div>
                    <Label className="text-gray-300">Nombre del Template</Label>
                    <Input
                      value={editForm.nombre}
                      onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
                      className="mt-1 bg-gray-900 border-gray-700 text-white"
                      disabled
                    />
                  </div>

                  <div>
                    <Label className="text-gray-300">Asunto</Label>
                    <Input
                      value={editForm.asunto}
                      onChange={(e) => setEditForm({ ...editForm, asunto: e.target.value })}
                      className="mt-1 bg-gray-900 border-gray-700 text-white"
                    />
                  </div>

                  {previewMode === 'edit' ? (
                    <>
                      <div>
                        <Label className="text-gray-300">Contenido HTML</Label>
                        <Textarea
                          value={editForm.cuerpo}
                          onChange={(e) => setEditForm({ ...editForm, cuerpo: e.target.value })}
                          className="mt-1 bg-gray-900 border-gray-700 text-white font-mono text-sm"
                          rows={15}
                        />
                      </div>

                      {/* Variables disponibles */}
                      <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                        <h4 className="text-sm font-semibold text-white mb-3">Variables Disponibles</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {VARIABLES_DISPONIBLES.map((v) => (
                            <button
                              key={v.var}
                              onClick={() => insertVariable(v.var)}
                              className="text-left p-2 bg-gray-800 hover:bg-gray-700 rounded text-xs text-gray-300 hover:text-white transition-colors"
                              title={v.desc}
                            >
                              <code className="text-yellow-500">{v.var}</code>
                              <p className="text-xs text-gray-500 mt-1">{v.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label className="text-gray-300 mb-2 block">Vista Previa</Label>
                      <div className="bg-white p-6 rounded-lg border border-gray-300 max-h-[600px] overflow-auto">
                        <div dangerouslySetInnerHTML={{ __html: getPreviewHtml() }} />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="activo"
                      checked={editForm.activo}
                      onChange={(e) => setEditForm({ ...editForm, activo: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="activo" className="text-gray-300">Template activo</Label>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-12 text-center">
                  <Mail className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Selecciona un template para editar</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Regenerar Templates */}
      <ConfirmModal
        isOpen={showRegenerarModal}
        onClose={() => setShowRegenerarModal(false)}
        onConfirm={async () => {
          setRegenerating(true);
          try {
            const response = await fetch(`${BACKEND_URL}/api/email-templates/regenerar`, {
              method: 'POST',
              credentials: 'include'
            });
            
            if (response.ok) {
              const data = await response.json();
              toast.success(`✅ ${data.mensaje}\n${data.templates_creados} templates creados`);
              setShowRegenerarModal(false);
              setTimeout(() => {
                loadTemplates();
              }, 1000);
            } else {
              const errorData = await response.json();
              toast.error(errorData.detail || 'Error al regenerar templates');
            }
          } catch (error) {
            console.error('Error:', error);
            toast.error('Error al regenerar templates');
          } finally {
            setRegenerating(false);
          }
        }}
        title="Regenerar Templates de Email"
        message={
          <div className="space-y-4">
            <p className="text-gray-300">
              Esta acción eliminará y recreará todos los templates con la configuración actual del sistema.
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-300 text-sm font-semibold mb-2">
                ⚠️ Advertencia Importante
              </p>
              <ul className="text-yellow-200 text-sm space-y-1 list-disc list-inside">
                <li>Se perderán todas las personalizaciones de templates</li>
                <li>Los templates usarán la URL configurada en "Configuración"</li>
                <li>Se crearán 11 templates por defecto</li>
              </ul>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-300 text-sm">
                💡 Tip: Antes de regenerar, verifica que la URL en "Configuración" sea correcta
              </p>
            </div>
          </div>
        }
        confirmText={regenerating ? "Regenerando..." : "Sí, Regenerar Templates"}
        cancelText="Cancelar"
        isDangerous={true}
      />
    </div>
  );
}
